<template>
  <div
    v-if="show"
    :class="wrapperClass"
    :data-testid="componentId"
  >
    <div v-if="loading" class="w-100 d-flex flex-column gap-2">
      <div
        class="grey--text text--darken-1 line-1 noTransform"
        :data-testid="`${componentId}__label`"
      >
        {{ label }}
      </div>
      <div class="font-weight-bold">
        <v-skeleton-loader
          type="text"
          :width="meta.skeletonWidth"
          :data-testid="`${componentId}__loader`"
        />
      </div>
    </div>

    <v-menu
      v-else-if="hasSelection && shouldShowMenu"
      offset-y
      open-on-hover
      :open-delay="navOpenDelay"
      :close-delay="navCloseDelay"
      :close-on-content-click="!multiSelect"
      transition="slide-y-transition"
      content-class="variance-analysis-filter-menu-content"
      min-width="220"
      max-width="300"
    >
      <template #activator="{ on, attrs }">
        <div
          class="d-flex flex-column gap-2 w-100 cursor-pointer"
          v-bind="attrs"
          v-on="on"
          :data-testid="`${componentId}__activator`"
        >
          <div
            class="grey--text text--darken-1 line-1 noTransform"
            :data-testid="`${componentId}__label`"
          >
            {{ label }}
          </div>
          <div class="font-weight-bold d-flex filter-hide">
            <span class="filter-text line-1">
              {{ selectedName }}
            </span>
            <img src="/img/icons/downArrow.svg" alt="Down arrow icon" class="arrow-icon">
          </div>
        </div>
      </template>

      <div class="white" style="min-width: 220px">
        <div v-if="isReportingUnit">
            <div
              v-if="isSearchable"
              class="white pa-2"
              @click.stop=";"
            >
              <lb-string
                :value="searchValue"
                @input="$emit('update:searchValue', $event)"
                hidedetails
                label="Search"
                :data-testid="`${componentId}__search`"
              />
            </div>
            <perfect-scrollbar style="max-height: 300px;">
              <v-list
                v-if="!apiLoading"
                dense
                class="pa-0 border-left-default scrollbar-list"
              >
                <!-- Reporting Unit Logic -->
                <template v-if="limitedTopItems.length > 0">
                  <v-list-item
                    v-if="showAllOption"
                    @click="handleItemClick('all')"
                    :data-testid="`${componentId}__item-all`"
                  >
                    <v-list-item-action v-if="multiSelect" class="mr-2 my-0">
                      <v-checkbox
                        :input-value="isSelected('all')"
                        color="primary"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                        @click.stop="handleItemClick('all')"
                      />
                    </v-list-item-action>
                    <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-for="item in limitedTopItems"
                    :key="item._id"
                    @click="handleItemClick(item._id)"
                    :data-testid="`${componentId}__item`"
                  >
                    <v-list-item-action v-if="multiSelect" class="mr-2 my-0">
                      <v-checkbox
                        :input-value="isSelected(item._id)"
                        color="primary"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                        @click.stop="handleItemClick(item._id)"
                      />
                    </v-list-item-action>
                    <v-list-item-title :class="itemTitleClass(item)">
                      {{ item.name || 'NA' }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!isExpanded && topItems.length > maxVisibleItems"
                    @click.stop="isExpanded = true"
                    class="justify-center cursor-pointer"
                    :data-testid="`${componentId}__more`"
                  >
                    <v-list-item-title class="grey--text caption v-list-item-title text-center">
                      Show {{ topItems.length - maxVisibleItems }} more
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <div
                  v-else
                  class="text-center grey--text title pa-6 v-list-item-title"
                  :data-testid="`${componentId}__no-data`"
                >
                  {{ noDataText || 'No Data' }}
                </div>
              </v-list>
              <v-skeleton-loader
                v-else
                class="scrollbar-list"
                type="article,article"
                :data-testid="`${componentId}__loader`"
              />
            </perfect-scrollbar>
          </div>

          <div v-else-if="usesTopItems">
            <div
              v-if="isSearchable"
              class="white pa-2"
              @click.stop=";"
            >
              <lb-string
                :value="searchValue"
                @input="$emit('update:searchValue', $event)"
                hidedetails
                label="Search"
                :data-testid="`${componentId}__search`"
              />
            </div>
            <perfect-scrollbar style="max-height: 300px">
              <v-list dense class="pa-0 border-left-default">
                <v-list-item
                  v-if="showAllOption"
                  @click="handleItemClick('all')"
                  :data-testid="`${componentId}__item-all`"
                >
                  <v-list-item-action v-if="multiSelect" class="mr-2 my-0">
                    <v-checkbox
                      :input-value="isSelected('all')"
                      color="primary"
                      hide-details
                      dense
                      class="mt-0 pt-0"
                      @click.stop="handleItemClick('all')"
                    />
                  </v-list-item-action>
                  <v-list-item-title class="v-list-item-title">All</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-for="item in limitedTopItems"
                  :key="item._id"
                  @click="handleItemClick(item._id)"
                  :data-testid="`${componentId}__item`"
                >
                  <v-list-item-action v-if="multiSelect" class="mr-2 my-0">
                    <v-checkbox
                      :input-value="isSelected(item._id)"
                      color="primary"
                      hide-details
                      dense
                      class="mt-0 pt-0"
                      @click.stop="handleItemClick(item._id)"
                    />
                  </v-list-item-action>
                  <v-list-item-title :class="itemTitleClass(item)">
                    {{ item.name || 'NA' }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="!isExpanded && topItems.length > maxVisibleItems"
                  @click.stop="isExpanded = true"
                  class="justify-center cursor-pointer"
                  :data-testid="`${componentId}__more`"
                >
                  <v-list-item-title class="grey--text caption noTransform text-center">
                    Show {{ topItems.length - maxVisibleItems }} more
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </perfect-scrollbar>
          </div>

          <perfect-scrollbar
            v-else
            style="max-height: 300px;"
          >
            <v-list dense class="pa-0 border-left-default">
              <v-list-item
                v-for="item in menuEntries"
                :key="item._id"
                @click="handleItemClick(item._id)"
                :data-testid="`${componentId}__item`"
              >
                <v-list-item-action v-if="multiSelect" class="mr-2 my-0">
                  <v-checkbox
                    :input-value="isSelected(item._id)"
                    color="primary"
                    hide-details
                    dense
                    class="mt-0 pt-0"
                    @click.stop="handleItemClick(item._id)"
                  />
                </v-list-item-action>
                <v-list-item-title class="v-list-item-title">
                  {{ menuItemLabel(item) }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </perfect-scrollbar>
      </div>

    </v-menu>

    <!-- Date Type Filter -->
    <v-menu
      v-else-if="isDateType"
      v-model="dateMenuOpen"
      offset-y
      :close-on-content-click="false"
      :open-delay="navOpenDelay"
      transition="slide-y-transition"
      content-class="variance-analysis-filter-menu-content"
    >
      <template #activator="{ on, attrs }">
        <div
          class="d-flex flex-column gap-2 w-100 cursor-pointer"
          v-bind="attrs"
          v-on="on"
          :data-testid="`${componentId}__activator`"
        >
          <div
            class="grey--text text--darken-1 line-1 noTransform"
            :data-testid="`${componentId}__label`"
          >
            {{ label }}
          </div>
          <div class="font-weight-bold d-flex filter-hide">
            <span class="filter-text line-1">{{ selectedName }}</span>
            <img src="/img/icons/downArrow.svg" alt="Down arrow icon" class="arrow-icon">
          </div>
        </div>
      </template>
      <v-date-picker
        :value="selected || ''"
        @input="onDateSelected"
        no-title
        color="primary"
        class="elevation-0"
        :data-testid="`${componentId}__datepicker`"
      />
    </v-menu>

    <!-- Static / No Menu State -->
    <div v-else class="d-flex flex-column gap-2">
      <div
        class="grey--text text--darken-1 line-1 noTransform"
        :data-testid="`${componentId}__label`"
      >
        {{ label }}
      </div>
      <div class="font-weight-semibold">
        <span
          v-if="hasSelection"
          :class="singleValueClass"
          :data-testid="`${componentId}__single-value`"
        >
          {{ displayValue }}
        </span>
        <span v-else :data-testid="`${componentId}__na`">NA</span>
      </div>
    </div>
  </div>
</template>

<script>
const MAX_VISIBLE_ITEMS = 25;

const FILTER_META = {
  period: {
    wrapperClass: 'filter-period d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '150px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      return instance.formatPeriod(instance.items?.[instance.selected]);
    },
    sortComparator(a, b) {
      const aYear = a?.year ?? 0;
      const bYear = b?.year ?? 0;
      if (aYear !== bYear) {
        return bYear - aYear;
      }
      const aNumber = a?.number ?? 0;
      const bNumber = b?.number ?? 0;
      return bNumber - aNumber;
    },
    menuItemLabel(instance, item) {
      return instance.formatPeriod(item);
    }
  },
  year: {
    wrapperClass: 'filter-year d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '150px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      const aYear = parseInt(a?.name, 10);
      const bYear = parseInt(b?.name, 10);
      if (!Number.isNaN(aYear) && !Number.isNaN(bYear) && aYear !== bYear) {
        return bYear - aYear;
      }
      return String(b?.name || '').localeCompare(String(a?.name || ''));
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },
  rutype: {
    wrapperClass: 'filter-type d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '80px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      if (a._id === 'all') return -1;
      if (b._id === 'all') return 1;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },
  network: {
    wrapperClass: 'filter-network d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: true,
    searchable: true,
    showAll: true,
    singleValueClass: 'filter-text',
    selectedName(instance) {
      return (instance.items?.[instance.selected] || {}).name || 'All';
    }
  },
  region: {
    wrapperClass: 'filter-region d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: true,
    searchable: true,
    showAll: true,
    singleValueClass: 'filter-text',
    selectedName(instance) {
      return (instance.items?.[instance.selected] || {}).name || 'All';
    }
  },
  country: {
    wrapperClass: 'filter-country d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: true,
    searchable: true,
    showAll: true,
    singleValueClass: 'filter-text',
    highlightClass: 'font-weight-bold noTransform',
    selectedName(instance) {
      return (instance.items?.[instance.selected] || {}).name || 'All';
    }
  },
  'reporting-unit': {
    wrapperClass: 'filter-reportingunit d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '150px',
    usesTopItems: true,
    searchable: true,
    showAll: false,
    singleValueClass: 'filter-text',
    truncate: true,
    truncateLimit: 18,
    selectedName(instance) {
      if (!instance.selected || instance.selected === 'all') return 'All';
      const item = instance.items?.[instance.selected];
      let name = item?.name;
      if (!name) {
        // Fallback to topItems if items map is incomplete
        const topItem = instance.topItems?.find(i => i._id === instance.selected);
        name = topItem?.name;
      }
      // Clean up any "undefined - " prefix from name
      if (name && typeof name === 'string' && name.startsWith('undefined - ')) {
        name = name.replace(/^undefined - /, '');
      }
      return name || 'All';
    }
  },
  'module': {
    wrapperClass: 'filter-module d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'role': {
    wrapperClass: 'filter-role d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'materiality': {
    wrapperClass: 'filter-materiality d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'cycletype': {
    wrapperClass: 'filter-cycletype d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '100px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    // No sortComparator - preserve order from FILTER_CYCLE_ARRAY: All → Actual → Forecast
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'requesttype': {
    wrapperClass: 'filter-requesttype d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '250px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    // No sortComparator - preserve original order
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'approvalstatus': {
    wrapperClass: 'filter-approvalstatus d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '120px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      // Sort "All" option to first position
      if (a._id === 'all') return -1;
      if (b._id === 'all') return 1;
      return 0;
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'startdate': {
    wrapperClass: 'filter-date d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '120px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      if (!instance.selected) return 'All';
      return instance.formatDate(instance.selected);
    }
  },

  'enddate': {
    wrapperClass: 'filter-date d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '120px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      if (!instance.selected) return 'All';
      return instance.formatDate(instance.selected);
    }
  },

  'batchtype': {
    wrapperClass: 'filter-batchtype d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '120px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  },

  'datacentrestatus': {
    wrapperClass: 'filter-datacentrestatus d-flex flex-column gap-2 text-center hoverable',
    skeletonWidth: '120px',
    usesTopItems: false,
    searchable: false,
    showAll: false,
    singleValueClass: 'noTransform',
    selectedName(instance) {
      const current = instance.items?.[instance.selected] || {};
      return current.name || 'NA';
    },
    sortComparator(a, b) {
      if (a._id === 'all') return -1;
      if (b._id === 'all') return 1;
      return 0;
    },
    menuItemLabel(instance, item) {
      return item?.name || 'NA';
    }
  }
};

export default {
  name: 'FilterTypes',
  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return Object.prototype.hasOwnProperty.call(FILTER_META, value);
      }
    },
    show: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Object,
      default: () => ({})
    },
    selected: {
      type: [String, Array],
      default: ''
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    topItems: {
      type: Array,
      default: () => []
    },
    searchValue: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    apiLoading: {
      type: Boolean,
      default: false
    },
    noDataText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isExpanded: false,
      dateMenuOpen: false
    };
  },
  watch: {
    searchValue() {
      this.isExpanded = false;
    }
  },
  computed: {
    meta() {
      return FILTER_META[this.type] || FILTER_META.period;
    },
    componentId() {
      return `variance-analysis-${this.type}-filter`;
    },
    wrapperClass() {
      return this.meta.wrapperClass;
    },
    hasSelection() {
      return Boolean(this.selected);
    },
    hasMultipleItems() {
      return Object.keys(this.items || {}).length > 1;
    },
    shouldShowMenu() {
      return this.hasSelection && this.hasMultipleItems;
    },
    menuEntries() {
      const entries = Object.values(this.items || {});
      if (typeof this.meta.sortComparator === 'function') {
        return entries.slice().sort(this.meta.sortComparator);
      }
      return entries;
    },
    selectedName() {
      if (this.multiSelect) {
        // Handle 'all' string for multi-select
        if (this.selected === 'all') {
          return 'All';
        }
        
        // Handle single string value in multi-select mode
        // (e.g., when network is auto-populated from reporting unit selection)
        if (typeof this.selected === 'string' && this.selected !== '' && this.selected !== 'all') {
          const id = this.selected;
          const item = this.items?.[id];
          let name = item?.name;
          
          // If name not found in main items map, check topItems
          if (!name && (this.usesTopItems || this.isReportingUnit)) {
             const topItem = (this.topItems || []).find(i => i._id === id);
             name = topItem?.name;
          }
          
          // Clean up "undefined - " prefix from name
          if (name && typeof name === 'string' && name.startsWith('undefined - ')) {
              name = name.replace(/^undefined - /, '');
          }
          
          return name || id;
        }
        
        if (Array.isArray(this.selected)) {
          if (this.selected.length === 0) {
            return 'All';
          }
          if (this.selected.length === 1) {
            const id = this.selected[0];
            const item = this.items?.[id];
            let name = item?.name;
            
            // If name not found in main items map, check topItems
            if (!name && (this.usesTopItems || this.isReportingUnit)) {
               const topItem = (this.topItems || []).find(i => i._id === id);
               name = topItem?.name;
            }
            
            // Clean up "undefined - " prefix from name (copied from reporting-unit meta logic)
            if (name && typeof name === 'string' && name.startsWith('undefined - ')) {
                name = name.replace(/^undefined - /, '');
            }
            
            return name || id;
          }
          return `${this.selected.length} selected`;
        }
      }
      if (typeof this.meta.selectedName === 'function') {
        return this.meta.selectedName(this);
      }
      return 'NA';
    },
    displayValue() {
      if (this.meta.truncate) {
        return this.truncateText(this.selectedName, this.meta.truncateLimit || 18);
      }
      return this.selectedName;
    },
    singleValueClass() {
      return this.meta.singleValueClass || 'noTransform';
    },
    isSearchable() {
      return Boolean(this.meta.searchable);
    },
    usesTopItems() {
      return Boolean(this.meta.usesTopItems);
    },
    showAllOption() {
      // In multi-select mode, always show "All" option if items exist
      if (this.multiSelect && this.limitedTopItems.length > 0) {
        return true;
      }
      return Boolean(this.meta.showAll && this.limitedTopItems.length > 0 && this.selected !== 'all');
    },
    limitedTopItems() {
      if (this.isExpanded) {
        return this.topItems || [];
      }
      return (this.topItems || []).slice(0, this.maxVisibleItems);
    },
    maxVisibleItems() {
      return MAX_VISIBLE_ITEMS;
    },
    isReportingUnit() {
      return this.type === 'reporting-unit';
    },
    isDateType() {
      return this.type === 'startdate' || this.type === 'enddate';
    },
    navState() {
      // Access global nav state: true = collapsed (mini), false = expanded
      return this.$store.state.mininavbar ? 'collapsed' : 'expanded';
    },
    navOpenDelay() {
      // Collapsed: 200ms, Expanded: 300ms
      return this.navState === 'collapsed' ? 200 : 300;
    },
    navCloseDelay() {
      // Collapsed: 350ms, Expanded: 500ms
      return this.navState === 'collapsed' ? 350 : 500;
    },
    /**
     * Performance optimization: Pre-compute a Set of selected IDs for O(1) lookup
     * This avoids O(n) Array.some() calls in isSelected() for each rendered item
     */
    selectedIdSet() {
      if (!this.multiSelect) {
        return null; // Not used in single-select mode
      }
      if (this.selected === 'all') {
        return null; // Special case handled separately
      }
      if (Array.isArray(this.selected)) {
        return new Set(this.selected.map(id => String(id)));
      }
      if (typeof this.selected === 'string' && this.selected !== '' && this.selected !== 'all') {
        return new Set([String(this.selected)]);
      }
      return new Set();
    }
  },
  methods: {
    formatPeriod(period) {
      if (!period) {
        return 'NA';
      }
      return period.name || 'NA';
    },
    formatDate(dateStr) {
      if (!dateStr) return 'All';
      try {
        const [year, month, day] = dateStr.split('-');
        if (year && month && day) {
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      } catch (e) {
        return dateStr;
      }
    },
    onDateSelected(value) {
      this.$emit('change', value);
      this.dateMenuOpen = false;
    },
    menuItemLabel(item) {
      if (typeof this.meta.menuItemLabel === 'function') {
        return this.meta.menuItemLabel(this, item);
      }
      return item?.name || 'NA';
    },
    truncateText(text, maxLength = 70) {
      if (!text) {
        return '';
      }
      if (text.length <= maxLength) {
        return text;
      }
      return `${text.substring(0, maxLength)}...`;
    },
    itemTitleClass(item) {
      const base = 'v-list-item-title';
      if (!item || !item.mark) {
        return base;
      }
      if (item.mark === 'yes') {
        return `${base} ${this.meta.highlightClass || 'font-weight-bold v-list-item-title'}`;
      }
      if (item.mark === 'no') {
        return `${base} grey--text text--darken-1 noTransform`;
      }
      return base;
    },
    handleItemClick(itemId) {
      if (!this.multiSelect) {
        this.$emit('change', itemId);
        return;
      }
      
      // Handle "Select All" in multi-select mode
      if (itemId === 'all') {
        const allSelected = this.areAllItemsSelected();
        
        if (allSelected) {
          // Deselect all - emit empty array
          this.$emit('change', []);
        } else {
          // Select all - emit 'all' string instead of all individual IDs
          this.$emit('change', 'all');
        }
        return;
      }
      
      // If current selection is empty string (displays as "All" but nothing actually selected),
      // start fresh with just the clicked item
      if (this.selected === '') {
        this.$emit('change', [itemId]);
        return;
      }
      
      // If 'all' is selected, clicking an individual item should deselect just that item
      // (keep all others selected)
      if (this.selected === 'all') {
        const allItemIds = this.getAllItemIds();
        // Use string comparison for consistency
        const itemIdStr = String(itemId);
        const newSelection = allItemIds.filter(id => String(id) !== itemIdStr);
        this.$emit('change', newSelection);
        return;
      }
      
      // Build current selection array - handle single string value in multi-select mode
      // (e.g., when network is auto-populated from reporting unit selection)
      let currentSelection = [];
      if (Array.isArray(this.selected)) {
        currentSelection = [...this.selected];
      } else if (typeof this.selected === 'string' && this.selected !== '' && this.selected !== 'all') {
        // Convert single string to array
        currentSelection = [this.selected];
      }
      
      // Use string comparison to find the index
      const itemIdStr = String(itemId);
      const index = currentSelection.findIndex(id => String(id) === itemIdStr);
      
      if (index > -1) {
        currentSelection.splice(index, 1);
      } else {
        currentSelection.push(itemId);
      }
      
      // Check if all items are now selected after this change
      // Only perform this check if NOT searching to avoid false positives with filtered lists
      if (!this.searchValue || this.searchValue.trim() === '') {
        const allItemIds = this.getAllItemIds();
        const selectionStrs = currentSelection.map(id => String(id));
        if (currentSelection.length === allItemIds.length && allItemIds.length > 0 && allItemIds.every(id => selectionStrs.includes(String(id)))) {
          // All items selected, emit 'all' instead of array of all IDs
          this.$emit('change', 'all');
          return;
        }
      }
      
      this.$emit('change', currentSelection);
    },
    isSelected(itemId) {
      if (!this.multiSelect) {
        return this.selected === itemId;
      }
      
      // Check if "All" is selected (selected value is 'all' string)
      if (this.selected === 'all') {
        return true; // All items including "All" checkbox should be checked
      }
      
      // Check if "All" checkbox should be checked
      if (itemId === 'all') {
        return this.areAllItemsSelected();
      }
      
      // Use pre-computed Set for O(1) lookup (instead of O(n) Array.some())
      const idSet = this.selectedIdSet;
      if (idSet) {
        return idSet.has(String(itemId));
      }
      
      return false;
    },
    getAllItemIds() {
      // Get all item IDs from topItems or menuEntries
      if (this.usesTopItems || this.isReportingUnit) {
        return (this.topItems || []).map(item => item._id).filter(id => id && id !== 'all');
      }
      return this.menuEntries.map(item => item._id).filter(id => id && id !== 'all');
    },
    areAllItemsSelected() {
      // If selected is 'all' string, all items are selected
      if (this.selected === 'all') {
        return true;
      }
      
      if (!Array.isArray(this.selected) || this.selected.length === 0) {
        return false;
      }
      const allItemIds = this.getAllItemIds();
      if (allItemIds.length === 0) {
        return false;
      }
      // Use pre-computed Set for O(1) lookups per item
      const idSet = this.selectedIdSet;
      if (!idSet || idSet.size < allItemIds.length) {
        return false;
      }
      return allItemIds.every(id => idSet.has(String(id)));
    }
  }
};
</script>

<style scoped>
.v-menu__content {
  border-radius: 12px !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #e2e8f0;
  margin-top: 8px;
}

.v-list {
  padding: 4px;
}

.v-list-item {
  cursor: pointer;
  user-select: none;
}

.v-list-item:hover {
  background-color: var(--cycle-filter-section-bg) !important;
}

.v-list-item-title {
  font-size: var(--font-size-xs) !important;
  font-weight: 400 !important;
  color: #0f172a !important;
  cursor: pointer !important;
}

.v-list-item-title.caption {
  font-size: var(--font-size-xxs) !important;
  font-weight: 400 !important;
}

.v-list-item-action {
  min-width: 32px;
  margin-right: 8px;
}

.arrow-icon {
  transition: transform 0.3s ease;
}
</style>

<style>
.variance-analysis-filter-menu-content {
  z-index: 9999 !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #e2e8f0;
  margin-top: 8px;
}
</style>
